/**
 * Hook for accessing dropdown options from Redux store
 * Gets dropdown data from the active tab's slice (referrals, nonReferrals, or processedReferrals)
 * @param responseKey - Which data to extract from Redux store
 * @param selectedProtocols - Optional array of selected protocol values to filter sites
 * @param currentTabKey - The current active tab key to determine which slice to use
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { transformDropdownOptions } from '../services/dropdownApi';

export const useDropdownApi = (
  responseKey:
    | 'cities'
    | 'states'
    | 'associatedProtocols'
    | 'qualifiedProtocols'
    | 'sites'
    | 'ethnicity'
    | 'userIds'
    | null,
  selectedProtocols?: string[],
  currentTabKey?: string
) => {
  const referralsDropdownData = useSelector((state: RootState) => state.lmaReferrals.dropdownData);
  const nonReferralsDropdownData = useSelector(
    (state: RootState) => state.lmaNonReferrals.dropdownData
  );
  const processedReferralsDropdownData = useSelector(
    (state: RootState) => state.lmaProcessedReferrals.dropdownData
  );

  const dropdownData = useMemo(() => {
    if (currentTabKey === 'referrals') return referralsDropdownData;
    if (currentTabKey === 'nonReferrals') return nonReferralsDropdownData;
    if (currentTabKey === 'processedReferrals') return processedReferralsDropdownData;
    return (
      referralsDropdownData || nonReferralsDropdownData || processedReferralsDropdownData || null
    );
  }, [
    currentTabKey,
    referralsDropdownData,
    nonReferralsDropdownData,
    processedReferralsDropdownData,
  ]);

  const options = useMemo(() => {
    if (!responseKey || !dropdownData) {
      return [];
    }

    return transformDropdownOptions(responseKey, dropdownData, selectedProtocols);
  }, [responseKey, dropdownData, selectedProtocols]);

  return { options, loading: false, error: null };
};
